import { AxiosResponse } from 'axios';
import { queryClient } from 'config/queryClient';
import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { configKeys } from './query-keys';
import { ConfigResponseType } from '../apis/ConfigAPI';
import { getConfigEntries, getConfigQueryOptions } from '../helpers';

function useConfigQuery(
  options: {
    companyId?: string;
    configName?: string;
    configType?: string;
    configCategory?: string;
    shouldFetch?: boolean;
    productLevel?: boolean;
  } = {},
) {
  const { data: configData, ...rest } = useQuery(getConfigQueryOptions(options));

  const { config, entries, activeUniqueEntries, configs, response } = useMemo(
    () => ({
      config: configData?.data.config,
      entries: configData?.data.config?.entries || [],
      activeUniqueEntries: getConfigEntries({ data: configData?.data }),
      configs: configData?.data?.configs || [],
      response: configData,
    }),
    [configData],
  );

  const updateConfigCache = ({
    updatedConfig,
    ...params
  }: {
    companyId?: string;
    configName?: string;
    configType?: string;
    configCategory?: string;
    updatedConfig: ConfigResponseType;
  }) => {
    const _options = {
      companyId: options.companyId,
      configName: options.configName,
      configType: options.configType,
      configCategory: options.configCategory,
      ...params,
    };
    const queryStatus = queryClient.getQueryState(configKeys.getConfig(_options));

    if (queryStatus?.status && queryStatus.status !== 'idle') {
      queryClient.setQueryData<AxiosResponse<ConfigResponseType, Error> | undefined>(
        configKeys.getConfig(_options),
        (old) => {
          if (old) {
            return {
              ...old,
              data: {
                ...old.data,
                ...updatedConfig,
              },
            };
          }

          return old;
        },
      );
    }
  };

  return {
    config,
    entries,
    activeUniqueEntries,
    configs,
    response,
    ...rest,
    updateConfigCache,
  };
}

export default useConfigQuery;
